import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setAddPanel, setTempPanelsGroup, togglePanelModal } from 'store/slices/PanelSlice';
import AddPanelModal from './AddPanelModal/index';
import { toggleAddPanelModalState } from 'store/slices/AddPanelModalSlice/addPanelModalSlice';
import CancelAddPanelModal from './CancelAddPanelModal';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { setEnableFacetEdit } from 'store/slices/RoofEditSlice';
import useScreenDims from 'hooks/useScreenDims';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { useEffect } from 'react';
import useAddPanelsQuery from 'hooks/api/panel/addPanel'; 
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice'; 
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';

export default function AddPanelButton() {
	const dispatch = useDispatch<AppDispatch>();
	const { enableAddPanel, newlyAddedPanels, tempPanelsGroup } = useSelector((state: RootState) => state.panelSlice.data);
	const { hadTransition } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { uuid, panel, designType } = useSelector((state: RootState) => state.roofData.data);
	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const addPanelsMutation = useAddPanelsQuery();
	const startShadingRecalc = useStartShadingRecalc();

	const screenDims = useScreenDims();
	const { width: screenWidth } = screenDims;
	const isMobileView = screenWidth < 860;

	const isAnyOtherActiveAction = useSelector(selectIsAnyToolActionActive) && !enableAddPanel;
	const isRecomputing = useSelector(selectIsRecomputing);

	const shouldDisable = queriesRunningInBackground || isAnyOtherActiveAction || hadTransition || isRecomputing;

	useEffect(() => {
		const cancelHandler = () => {
			dispatch(setAddPanel({ shouldEnable: false }));
			if (tempPanelsGroup) dispatch(setTempPanelsGroup({ addedPanels: [] }));
		};

		const doneHandler = async () => {
			if (!newlyAddedPanels || newlyAddedPanels.length === 0) {
				dispatch(setAddPanel({ shouldEnable: false }));
				dispatch(setTempPanelsGroup({ addedPanels: [] }));
				return;
			}

			const shadingStatus = await addPanelsMutation.mutateAsync();
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();

			dispatch(setAddPanel({ shouldEnable: false }));
			dispatch(setTempPanelsGroup({ addedPanels: [] }));
			await startShadingRecalc(shadingStatus);
		};

		registerToolModeHandlers(TOOL_ACTION.ADD_PANEL, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard the added panels?',
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.ADD_PANEL);
		};
	}, [dispatch, tempPanelsGroup, newlyAddedPanels, addPanelsMutation, uuid, panel?.key, designType,
		startShadingRecalc]);

	function handleClick() {
		if (enableAddPanel) {
			if(tempPanelsGroup) {
				dispatch(setTempPanelsGroup({addedPanels: []}));
			}
			if(newlyAddedPanels.length > 0) {
				dispatch(togglePanelModal({ targetModal: 'CANCEL_ADD_PANEL', val: true }));
			}
			return;
		}
		// if (!enableAddPanel) {
		// 	dispatch(toggleAddPanelModalState({ value: true }));
		// }

		dispatch(setAddPanel({ shouldEnable: !enableAddPanel }));

	}

	const handleModalConfirm = () => {
		dispatch(setAddPanel({ shouldEnable: true }));
		console.log(newlyAddedPanels);
	};

	const handleModalCancel = () => {
		dispatch(setAddPanel({ shouldEnable: false }));
		console.log(newlyAddedPanels);
	};

	useEffect(() => {
		registerToolModeHandlers(TOOL_ACTION.ADD_PANEL, { 
			cancel: () => {
				dispatch(setAddPanel({ shouldEnable: false }));
				dispatch(setTempPanelsGroup({addedPanels:[]}));
				dispatch(togglePanelModal({targetModal: 'CANCEL_ADD_PANEL', val:false}));
			},
			cancelConfirmMessage: 'Are you sure you want to discard the added panels?'
		});
		
		return () => {
			clearToolModeHandlers(TOOL_ACTION.ADD_PANEL);
		};
	}, [dispatch]);

	return (
		<>
			<Button
				className={`${enableAddPanel ? 'active' : ''} btn-tools`}
				style={{
					background: `${enableAddPanel ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
				}}
				id="add-panel"
				onClick={handleClick}
				disabled={shouldDisable}
				tooltipContent={enableAddPanel ? '' : 'Add Panels'}
			>
				<svg 
					width={isMobileView ? '19.1' : '21'} 
					height={isMobileView ? '20' : '22'}
					viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M6.80246 21.631H1V1H16.4732V7.44718" stroke={`${enableAddPanel ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M21.6309 16.4731L8.7365 16.4731M15.1837 10.0259L15.1837 22.9202" stroke={`${enableAddPanel ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			</Button>
			<AddPanelModal
				onConfirm={handleModalConfirm}
				onCancel={handleModalCancel} />
			<CancelAddPanelModal />
		</>
	);

}